// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
The advice that actually works is dull: look at far more than you buy, buy the best condition you can afford rather than the biggest, and pick one narrow thing to learn properly. Everything else is decoration on those three.

## Spend the first month not buying

The single most useful thing a new collector can do is build a sense of what is normal before spending anything.

Watch the same category for four to six weeks. Note what pieces are asking, and where you can, what they actually sell for. Asking prices tell you what sellers hope; sold prices tell you what the market is. After a month you will have something no article can give you: a feel for when something is cheap.

While you are looking, handle pieces where you can. Antique fairs, auction viewings, a friendly local dealer. Ten minutes holding real Georgian work teaches more than a week of reading.

## Pick a lane

A collection of one of everything is a drawer. A collection of one thing, understood well, is a collection, and it is far easier to buy well in a narrow field because you learn what is normal fast.

Narrow does not mean expensive. Some good lanes:

- **One era.** Edwardian, or Art Deco, or Victorian mourning.
- **One object.** Tie pins. Cufflinks. Signet rings. Lockets.
- **One material.** Enamel work. Old cut diamonds. Danish or Swedish silver.
- **One motif.** Snakes, hands, hearts, insects, stars.
- **One maker or one country.**

The unfashionable lanes are the affordable ones. Our [guide to what makes a piece rare](/blog/what-makes-antique-jewelry-rare) explains why scarce and expensive are not the same thing, and where the gap sits.

## Condition beats size, every time

A smaller piece in original condition will hold its value and its charm far better than a larger piece that has been rebuilt.

What to protect against:

- **Replaced stones**, particularly modern cuts dropped into an antique setting.
- **Worn claws and thin shanks**, which are repairs waiting to happen.
- **Over-polishing.** Machine polishing removes metal, softens engraving and can erase a hallmark. Even wear is patina, and patina is worth money.
- **Damaged enamel**, which cannot be invisibly repaired.

Our [ring settings guide](/blog/ring-settings-explained) covers what to inspect, and the [reproduction guide](/blog/how-to-spot-fake-antique-jewelry) covers how to tell rebuilt from original.

## Learn one skill properly: reading the back

If you learn a single practical skill, make it this. Turn every piece over.

The back tells you how it was made, whether parts have been replaced, whether it has been converted from something else, and usually where the mark is. Our guides to [gold marks](/blog/how-to-tell-if-gold-jewelry-is-real), [silver marks](/blog/silver-hallmarks-explained) and [handmade versus cast](/blog/handmade-vs-cast-jewelry) are all really about reading the back.

Buy a 10x loupe. They cost very little and they change what you can see.

## What to buy first

A reasonable first three, if you want a structure:

1. **Something you will wear.** A collection you never touch stops being interesting.
2. **Something small and genuinely old**, to train your eye. A Georgian or early Victorian piece in modest condition teaches you what two hundred years of wear looks like.
3. **Something with a mark you can read**, so you can follow the whole chain from stamp to date to maker.

Avoid, at the start: heavily damaged pieces you plan to restore, anything sold mainly on a story, and the most expensive thing you can afford in month one.

## Set a budget rule and keep it

Two that work:

- **A per-piece ceiling** for the first year, low enough that a mistake is a lesson rather than a loss.
- **Ten percent for education.** Books, a loupe, a fair entry, a valuation on something you were unsure about. It pays for itself.

Also know the floor under any gold or silver piece: weight times purity times the metal price. Our [resale guide](/blog/how-much-is-my-jewelry-worth) sets out the calculation. Buying close to the metal floor means you can be wrong about the age and still not lose money.

## Keep records from the first purchase

Not glamorous, and the thing every collector wishes they had started earlier.

For each piece, note what you paid, where it came from, the date, the weight, the measurements, and any marks. Photograph it in daylight, including the hallmark. Save the seller's description before the listing disappears.

This does three jobs: it is your insurance record, it is your provenance if you ever sell, and it is how you learn, because you can look back and see what you got right.

Our [insurance guide](/blog/how-to-insure-antique-jewelry) covers what a record needs to contain, and why it should be stored somewhere that is not your house.

## Care, briefly

Store pieces separately so nothing harder touches anything softer. Keep silver in a closed bag with an anti-tarnish strip. Keep pearls and enamel away from everything.

Do not clean anything until you know what it is. The [cleaning guide](/blog/how-to-clean-antique-jewelry) covers what must never go in water or an ultrasonic, and the household advice that quietly destroys value.

## Where to buy, and the trade-off each way

**Auction** rewards knowledge and punishes guessing. Cheapest per piece if you know what you are looking at, and there is usually no return.

**Dealers and fairs** cost more and you can hold the piece and ask questions. Worth the premium early on.

**General marketplaces** have volume and very mixed accuracy. You are your own expert.

**A specialist marketplace** puts you in front of people who collect the same things. Smaller pool, better descriptions, and the buyer protection matters when you are still learning.

Our [comparison of the routes](/blog/where-to-sell-pre-loved-fine-jewelry-online) goes through fees and payout on each, from the selling side.

## The rule that saves the most money

Do not buy the first example you see of anything. See three. The third one teaches you what the first two were worth.

If a piece you are hunting for is not listed, post it on the [ISO board](/wanted) and let sellers bring it to you. Or [browse what is listed now](/s), and read [how payments are protected](/how-payments-work) before you buy.

The Hallmark Society keeps a [glossary](https://hallmarksociety.com/glossary/) of the terms you will meet, which is worth bookmarking while the vocabulary is still new.
`;

export default body;
